import React, { PropsWithChildren, useState } from 'react';
import { isWatermarkPresetRidPresent } from '@sharefiledev/fileviewer-pilet-sdk';
import { RID } from '@sharefiledev/sfplugin-resource-id';
import { PermissionByType, PresetsByType } from '@sharefiledev/sharing-pilet-sdk';
import {
	ConditionalLinkOptions,
	Link,
	LinksCreatePostRequest,
	LinksCreatePostRequestTypeEnum,
	LinksCreatePostRequestViewTemplateDefinitionEnum,
	LinksPatchRequest,
	LinksSearchPostRequest,
	ReplacePostRequest,
	SearchLinkFilterPayloadResourceListMatchModeEnum,
	SharablePreset,
	ShareRequest,
} from '@sharefiledev/sharing-pilet-sdk/api';
import { App } from 'antd';
import { useAsync } from 'react-async-hook';
import { useAppContext } from '../../AppContext';
import { useLocalStorageState } from '../../Development/useLocalStorageState';
import { PermissionsSelectionByType } from '../../PermissionsContext/PluginContextProvider';
import { usePluginContext } from '../../PermissionsContext/usePluginContext';
import { PermissionsHttp } from '../../PermissionsHttp';
import { PermissionId } from '../../ram-types';
import { useLinkSettings } from '../ConditionalLinks/customHooks/useLinkSettings';
import { usePermissionConfigurationForWatermarking } from '../ConditionalLinks/customHooks/usePermissionConfigurationForWatermarking';
import { useWatermarkingState } from '../ConditionalLinks/customHooks/useWatermarkState';
import {
	getPreset,
	getPresetInfoByPermission,
	getPresetInfoTypeByPermission,
	getResourceListForVersionFlag,
	getRidsForEntityType,
	isConfigurationByResourceType,
	isStoragePlugin,
} from '../ConditionalLinks/utils/linkShare-utils';
import {
	ConditionalLinkOptionsModalContentType,
	DEFAULT_EXPIRATION_DAYS,
	ResourceTypeLiteral,
} from '../constants';
import {
	getRidsWithoutFileVersion,
	getRidWithoutFileVersion,
} from '../helpers/GetBaseRids';
import { LinkBasedShareContext } from './LinkBasedShareContext';

interface LinkBasedShareContextProviderProps extends PropsWithChildren {
	parentContainerRid: string;
	consumerPluginName?: string;
	isManageUserTriggeredByExtensionSlot?: boolean;
}

export interface CreateConditionalLinkProps {
	conditionalLinkOptions: ConditionalLinkOptions;
	resourceRids: string[];
	presetName: string;
	permissions: PermissionId<string>[];
	permissionsByType: PermissionsSelectionByType;
	presets: SharablePreset[];
	presetsByType: PresetsByType[];
	isWatermarkedLink: boolean;
}

export interface CreateAndReplaceLinkProps {
	conditionalLinkOptions: ConditionalLinkOptions;
	link: Link;
	resourceRids: string[];
	presetName: string;
	permissions: PermissionId<string>[];
	permissionsByType: PermissionsSelectionByType;
	presets: SharablePreset[];
	presetsByType: PresetsByType[];
	isWatermarkedLink: boolean;
}

export interface UpdateConditionalLinksParams {
	linkPatchRequest: LinksPatchRequest;
	previousPresetRid: string;
	transformedRids: string[];
	previousLatestVersionFlag: boolean;
	presetsByType: PresetsByType[];
	availablePermissionsSetsByType: PermissionByType;
	isWatermarkedLink: boolean;
	permissionsByType: PermissionsSelectionByType;
	oldResourceRids: string[];
}

export const LinkBasedShareContextProvider: React.FC<
	LinkBasedShareContextProviderProps
> = props => {
	const { notification } = App.useApp();
	const { children, parentContainerRid, consumerPluginName } = props;
	const {
		t,
		selectedPermissionsByType,
		availablePermissionSetsByType,
		presetsByResourceType,
		selectedResources,
		sharablePresets,
		selectedPreset,
		executeReplace,
		configurationByResourceType,
	} = usePluginContext();
	const { account } = useAppContext();
	const key = `${account.Id}/ConditionalLinks`;
	const [expiryDate, setExpiryDate] = useLocalStorageState(
		`${key}/expiryDate`,
		DEFAULT_EXPIRATION_DAYS
	);
	const [latestVersionFlag, setLatestVersionFlag] = useLocalStorageState(
		`${key}/latestVersionFlag`,
		true
	);
	const [waterMarkFlag, setWaterMarkFlag] = useLocalStorageState(
		`${key}/waterMarkFlag`,
		false
	);
	const [notifyMeFlag, setNotifyMeFlag] = useLocalStorageState(
		`${key}/notifyMeFlag`,
		true
	);
	const rids = selectedResources.map(r => r.rid);

	const [linkUrl, setLinkUrl] = useState<string>('');
	const [conditionalLinks, setConditionalLinks] = useState<Link[]>([]);
	const [linkRid, setLinkRid] = useState<string>('');
	const [shareWithSpecificPeopleOpen, setShareWithSpecificPeopleOpen] =
		useState<boolean>(false);
	const [isLinkBasedManageUserModalOpen, setIsLinkBasedManageUserModalOpen] =
		useState<boolean>(false);
	const [createLinkButtonLoading, setCreateLinkButtonLoading] = useState<boolean>(false);
	const [isViewConditionalOptionModalOpen, setIsViewConditionalOptionModalOpen] =
		useState<boolean>(false);
	const [linkOptionsContentType, setLinkOptionsContentType] =
		useState<ConditionalLinkOptionsModalContentType>(
			ConditionalLinkOptionsModalContentType.None
		);
	const { accountLevelLinkSettings } = useLinkSettings(consumerPluginName);
	const { isWatermarkingAvailable, watermarkedResponse } = useWatermarkingState({
		accountLevelLinkSettings,
	});
	const isManageUserTriggeredByExtensionSlot =
		props.isManageUserTriggeredByExtensionSlot || false;

	const isConfigurationByResourceTypePresent = isConfigurationByResourceType(
		availablePermissionSetsByType,
		presetsByResourceType
	);
	const fetchLink = async () => {
		try {
			// Attempt to get the link using the getLink API
			const getLinkObj: LinksSearchPostRequest = {
				filter: {
					containerRid: parentContainerRid,
					resourceList: getRidsWithoutFileVersion(rids),
					resourceListMatchMode: SearchLinkFilterPayloadResourceListMatchModeEnum.All,
					filterConditionalLinksByCurrentUser: true,
				},
			};

			const response = await PermissionsHttp.getLink(getLinkObj);

			if (response.length === 0) {
				createLink();
			} else {
				const nonReferralLinks = response.filter(
					link => link.type !== LinksCreatePostRequestTypeEnum.ReferralLink
				);

				setConditionalLinks(nonReferralLinks);
				setLinkUrl(response[0]?.url || '');
				setLinkRid(response[0]?.rid || '');
			}
		} catch (innerEx) {
			notification.error({
				message: t('linkBasedShareModal.linkGenerationFailed'),
				description: t('linkBasedShareModal.linkGenerationFailedDescription'),
			});
		}
	};

	const createLink = async () => {
		try {
			const linkCreateObj: LinksCreatePostRequest = {
				resourceList: selectedResources.map(data => data.rid),
				viewTemplateDefinition:
					LinksCreatePostRequestViewTemplateDefinitionEnum.StorageLinkGlobal,
				type: LinksCreatePostRequestTypeEnum.ReferralLink,
				conditionalLinkOptions: undefined,
				consumerPluginName,
			};
			const response = await PermissionsHttp.linkCreate(linkCreateObj);
			setConditionalLinks([]);
			setLinkUrl(response.url || '');
			setLinkRid(response.rid || '');
		} catch (error) {
			notification.error({
				message: t('linkBasedShareModal.linkGenerationFailed'),
				description: t('linkBasedShareModal.linkGenerationFailedDescription'),
			});
		}
	};

	const fetchLinkForWatermarking = async () => {
		if (!isWatermarkingAvailable) {
			return;
		}

		const watermarkedLinksRequest: LinksSearchPostRequest = {
			filter: {
				containerRid: parentContainerRid,
				resourceList: getRidsWithoutFileVersion(
					watermarkedResponse?.transformedRids || []
				),
				resourceListMatchMode: SearchLinkFilterPayloadResourceListMatchModeEnum.All,
				filterConditionalLinksByCurrentUser: true,
			},
		};

		try {
			const watermarkedLinks = await PermissionsHttp.getLink(watermarkedLinksRequest);

			setConditionalLinks(prevLinks => [...prevLinks, ...watermarkedLinks]);
		} catch (error) {
			notification.error({
				message: t('linkBasedShareModal.linkGenerationFailed'),
				description: t('linkBasedShareModal.linkGenerationFailedDescription'),
			});
		}
	};

	useAsync(fetchLink, [selectedResources]);
	useAsync(fetchLinkForWatermarking, [selectedResources, isWatermarkingAvailable]);
	const { configurationsByTypeForWatermarking, sharablePresetsByTypeForWatermarking } =
		usePermissionConfigurationForWatermarking({
			accountLevelLinkSettings,
			watermarkedResponse,
			isWatermarkingAvailable,
		});
	async function executeShare(
		resourceRids: string[],
		principalRID: string,
		presetName: string,
		permissions: PermissionId<string>[],
		useLatestVersion: boolean,
		permissionsByType: PermissionsSelectionByType,
		presets: SharablePreset[],
		presetsByType: PresetsByType[]
	) {
		try {
			const selectedRids = !isStoragePlugin(consumerPluginName)
				? resourceRids
				: getResourceListForVersionFlag(resourceRids, useLatestVersion);
			if (isConfigurationByResourceTypePresent) {
				const keys: string[] = Object.keys(permissionsByType);
				const shareRequestPayload: ShareRequest[] = keys
					.filter(resource => permissionsByType[resource].length > 0)
					.map(resource => ({
						rids: getRidsForEntityType(resource, selectedRids, !useLatestVersion),
						principalRIDs: [principalRID],
						permissions: permissionsByType[resource].map(p => p.toString()) ?? [],
						presetRID: getPresetInfoTypeByPermission(presetName, presetsByType)?.rid,
					}));
				await PermissionsHttp.bulkShare({ shareRequests: shareRequestPayload });
			} else {
				const selectedPermissionsString = permissions.map(permission =>
					permission.toString()
				);
				const shareRequestPayload: ShareRequest = {
					rids: selectedRids,
					principalRIDs: [principalRID],
					permissions: selectedPermissionsString,
					presetRID: getPresetInfoByPermission(presetName, presets)?.rid,
				};
				await PermissionsHttp.bulkShare({ shareRequests: [shareRequestPayload] });
			}
		} catch (error) {
			notification.error({
				message: t('linkBasedShareModal.conditionalLinks.linkPermissionError'),
				description: t(
					'linkBasedShareModal.conditionalLinks.linkPermissionErrorDescription'
				),
			});
		}
	}

	const createConditionalLink = async (
		createConditionalLinkProps: CreateConditionalLinkProps
	): Promise<Link | undefined> => {
		try {
			setCreateLinkButtonLoading(true);
			const linkCreateObj: LinksCreatePostRequest = {
				resourceList: createConditionalLinkProps.resourceRids,
				viewTemplateDefinition:
					LinksCreatePostRequestViewTemplateDefinitionEnum.StorageLinkGlobal,
				type: LinksCreatePostRequestTypeEnum.ConditionalLink,
				conditionalLinkOptions: createConditionalLinkProps.conditionalLinkOptions,
				consumerPluginName: createConditionalLinkProps.isWatermarkedLink
					? watermarkedResponse.pluginName
					: consumerPluginName,
			};

			const linkResponse = await PermissionsHttp.linkCreate(linkCreateObj);
			const updatedConditionalLink = {
				...linkResponse,
			};
			if (updatedConditionalLink.conditionalLinkOptions) {
				updatedConditionalLink.conditionalLinkOptions.presetRID =
					isConfigurationByResourceTypePresent
						? getPresetInfoTypeByPermission(
								createConditionalLinkProps.presetName,
								createConditionalLinkProps.presetsByType
						  )?.rid
						: getPresetInfoByPermission(
								createConditionalLinkProps.presetName,
								createConditionalLinkProps.presets
						  )?.rid;
			}
			setConditionalLinks([...conditionalLinks, updatedConditionalLink]);
			setConditionalLinks([...conditionalLinks, linkResponse]);
			setExpiryDate(
				createConditionalLinkProps.conditionalLinkOptions.expirationDays ||
					DEFAULT_EXPIRATION_DAYS
			);
			setLatestVersionFlag(
				createConditionalLinkProps.conditionalLinkOptions.useLatestVersion ?? true
			);
			setWaterMarkFlag(createConditionalLinkProps.isWatermarkedLink);
			setNotifyMeFlag(createConditionalLinkProps.conditionalLinkOptions.notify ?? true);
			await executeShare(
				createConditionalLinkProps.resourceRids,
				linkResponse.conditionalLinkOptions?.userGroupRid || '',
				createConditionalLinkProps.presetName,
				createConditionalLinkProps.permissions,
				createConditionalLinkProps.conditionalLinkOptions.useLatestVersion ?? true,
				createConditionalLinkProps.permissionsByType,
				createConditionalLinkProps.presets,
				createConditionalLinkProps.presetsByType
			);

			return linkResponse;
		} catch (error) {
			notification.error({
				message: t('linkBasedShareModal.linkGenerationFailed'),
				description: t('linkBasedShareModal.linkGenerationFailedDescription'),
			});
		} finally {
			setCreateLinkButtonLoading(false);
		}
	};

	const isEmptyOrUndefinedObject = (obj: any) => {
		return Object.values(obj).every(value => value === undefined);
	};

	const updateConditionalLinks = async ({
		linkPatchRequest,
		previousPresetRid,
		transformedRids,
		previousLatestVersionFlag,
		presetsByType,
		availablePermissionsSetsByType,
		isWatermarkedLink,
		permissionsByType,
		oldResourceRids,
	}: UpdateConditionalLinksParams) => {
		try {
			setCreateLinkButtonLoading(true);
			const response = await PermissionsHttp.updateLinks(linkPatchRequest);
			const userGroupRidFromPatchResponse: string =
				response?.conditionalLinkOptions?.userGroupRid ?? '';
			const isPermissionsChanged =
				linkPatchRequest.presetRID && linkPatchRequest.presetRID !== previousPresetRid;
			if (isConfigurationByResourceTypePresent) {
				await executeReplaceForMultipleTypes(
					linkPatchRequest,
					previousLatestVersionFlag,
					!!isPermissionsChanged,
					linkPatchRequest.resourceIds,
					presetsByType,
					availablePermissionsSetsByType,
					permissionsByType,
					previousPresetRid,
					transformedRids,
					oldResourceRids,
					userGroupRidFromPatchResponse
				);
			} else {
				// If the latest version flag has changed remove permission on the resource for the userGroup
				if (previousLatestVersionFlag !== linkPatchRequest.useLatestVersion) {
					const existingResourceIdsInLink = previousLatestVersionFlag
						? getRidsWithoutFileVersion(linkPatchRequest.resourceIds)
						: linkPatchRequest.resourceIds;
					await executeReplace(
						[],
						[],
						[],
						[userGroupRidFromPatchResponse],
						existingResourceIdsInLink
					);
				}
				if (
					previousLatestVersionFlag !== linkPatchRequest.useLatestVersion ||
					isPermissionsChanged
				) {
					const resourceIdsForLink = linkPatchRequest.useLatestVersion
						? getRidsWithoutFileVersion(linkPatchRequest.resourceIds)
						: linkPatchRequest.resourceIds;

					const permissionsToApply = getPresetInfoByPermission(
						selectedPreset!,
						sharablePresets
					)?.permissions;

					// Execute the replace operation
					await executeReplace(
						[userGroupRidFromPatchResponse],
						permissionsToApply ?? [],
						[],
						[],
						resourceIdsForLink
					);
				}
			}
			const linkIndex = conditionalLinks.findIndex(
				link => link.rid === linkPatchRequest.linkRid
			);
			const updatedLinks = [...conditionalLinks];
			if (linkIndex !== -1) {
				if (response && !isEmptyOrUndefinedObject(response)) {
					const updatedConditionalLink = {
						...updatedLinks[linkIndex],
						...response,
					};
					if (updatedConditionalLink.conditionalLinkOptions) {
						updatedConditionalLink.conditionalLinkOptions.presetRID =
							isConfigurationByResourceTypePresent
								? getPresetInfoTypeByPermission(selectedPreset, presetsByType)?.rid
								: getPresetInfoByPermission(selectedPreset, sharablePresets)?.rid;
					}
					updatedLinks[linkIndex] = updatedConditionalLink;
				} else {
					const updatedLink = updatedLinks[linkIndex];
					if (updatedLink.conditionalLinkOptions) {
						updatedLink.conditionalLinkOptions.presetRID =
							isConfigurationByResourceTypePresent
								? getPresetInfoTypeByPermission(selectedPreset, presetsByType)?.rid
								: getPresetInfoByPermission(selectedPreset, sharablePresets)?.rid;
					}

					return (updatedLinks[linkIndex] = updatedLink);
				}
			}
			setConditionalLinks(updatedLinks);
			setExpiryDate(linkPatchRequest.expirationDays || DEFAULT_EXPIRATION_DAYS);
			setLatestVersionFlag(linkPatchRequest.useLatestVersion ?? true);
			setWaterMarkFlag(isWatermarkedLink);
			setNotifyMeFlag(linkPatchRequest.notify ?? true);
			return response;
		} catch (error) {
			notification.error({
				message: t('linkBasedShareModal.linkUpdationFailed'),
				description: t('linkBasedShareModal.linkUpdationFailedDescription'),
			});
		} finally {
			setCreateLinkButtonLoading(false);
		}
	};

	const executeReplaceForMultipleTypes = async (
		linkPatchRequest: LinksPatchRequest,
		previousLatestVersionFlag: boolean,
		isPermissionsChanged: boolean,
		resourceRids: string[],
		presetsByType: PresetsByType[],
		availablePermissionsSetsByType: PermissionByType,
		permissionsByType: PermissionsSelectionByType,
		previousPresetRid: string,
		transformedRids: string[],
		oldResourceRids: string[],
		userGroupRidFromPatchResponse: string
	) => {
		const types: string[] = Object.keys(permissionsByType);
		const isWatermarkedLink = isWatermarkPresetRidPresent(linkPatchRequest.presetRID);
		if (
			previousLatestVersionFlag !== linkPatchRequest.useLatestVersion ||
			isPermissionsChanged
		) {
			// If the latest version flag has changed remove permission on the FILE resources for the userGroup
			const isOldLinkWatermarked = isWatermarkPresetRidPresent(previousPresetRid);
			let newResourceRIDs = oldResourceRids;
			newResourceRIDs = previousLatestVersionFlag
				? getRidsWithoutFileVersion(newResourceRIDs)
				: newResourceRIDs;
			const fileRids = getRidsForEntityType(
				isOldLinkWatermarked
					? RID.parse(newResourceRIDs[0]).entityType
					: ResourceTypeLiteral.File,
				newResourceRIDs,
				!previousLatestVersionFlag
			);
			const resourceType = isOldLinkWatermarked
				? RID.parse(fileRids[0])?.entityType
				: RID.parse(getRidWithoutFileVersion(fileRids[0]) ?? '')?.entityType;
			await executeReplaceByType(
				resourceType,
				[],
				[],
				fileRids,
				isOldLinkWatermarked
					? configurationsByTypeForWatermarking.presetsByType?.[resourceType] ?? []
					: configurationByResourceType?.presetsByType?.[resourceType] ?? [],
				isOldLinkWatermarked
					? configurationsByTypeForWatermarking.availablePermissionSetsByType
					: configurationByResourceType?.availablePermissionSetsByType ?? {},
				[userGroupRidFromPatchResponse]
			);
		}
		if (
			previousLatestVersionFlag !== linkPatchRequest.useLatestVersion ||
			isPermissionsChanged
		) {
			const isNewLinkWatermarked = isWatermarkPresetRidPresent(
				linkPatchRequest.presetRID
			);
			const newResourceRIDS = isNewLinkWatermarked ? transformedRids : resourceRids;
			const resourceRIDs = linkPatchRequest.useLatestVersion
				? getRidsWithoutFileVersion(newResourceRIDS)
				: newResourceRIDS;

			await Promise.all(
				types.map(type => {
					const entityRids = getRidsForEntityType(
						type,
						resourceRIDs,
						!linkPatchRequest.useLatestVersion
					);
					const resourceType = isWatermarkedLink
						? RID.parse(entityRids[0])?.entityType
						: RID.parse(getRidWithoutFileVersion(entityRids[0]) ?? '')?.entityType;

					const permissionsToApply =
						selectedPermissionsByType[type].map(p => p.toString()) ?? [];
					executeReplaceByType(
						resourceType,
						[userGroupRidFromPatchResponse],
						permissionsToApply ?? [],
						entityRids,
						presetsByType,
						availablePermissionsSetsByType
					);
				})
			);
		}
	};

	const createAndReplaceLink = async (
		createAndReplaceLinkProps: CreateAndReplaceLinkProps
	) => {
		try {
			const linkCreateObj: LinksCreatePostRequest = {
				resourceList: createAndReplaceLinkProps.resourceRids,
				viewTemplateDefinition:
					LinksCreatePostRequestViewTemplateDefinitionEnum.StorageLinkGlobal,
				type: LinksCreatePostRequestTypeEnum.ConditionalLink,
				conditionalLinkOptions: createAndReplaceLinkProps.conditionalLinkOptions,
				consumerPluginName: createAndReplaceLinkProps.isWatermarkedLink
					? watermarkedResponse.pluginName
					: consumerPluginName,
			};
			const newLink = await PermissionsHttp.linkCreate(linkCreateObj);
			setConditionalLinks(prevLinks =>
				prevLinks.map(existingLink => {
					if (existingLink.rid === createAndReplaceLinkProps.link.rid) {
						const updatedConditionalLink = {
							...existingLink,
							...newLink,
						};
						if (updatedConditionalLink.conditionalLinkOptions) {
							updatedConditionalLink.conditionalLinkOptions.presetRID =
								isConfigurationByResourceTypePresent
									? getPresetInfoTypeByPermission(
											createAndReplaceLinkProps.presetName,
											createAndReplaceLinkProps.presetsByType
									  )?.rid
									: getPresetInfoByPermission(
											createAndReplaceLinkProps.presetName,
											createAndReplaceLinkProps.presets
									  )?.rid;
						}
						return updatedConditionalLink;
					}
					return existingLink;
				})
			);

			await executeShare(
				createAndReplaceLinkProps.resourceRids,
				newLink.conditionalLinkOptions?.userGroupRid || '',
				createAndReplaceLinkProps.presetName,
				createAndReplaceLinkProps.permissions,
				createAndReplaceLinkProps.conditionalLinkOptions.useLatestVersion ?? true,
				createAndReplaceLinkProps.permissionsByType,
				createAndReplaceLinkProps.presets,
				createAndReplaceLinkProps.presetsByType
			);
		} catch (error) {
			console.error(error);
		}
	};

	async function executeReplaceByType(
		resourceType: string,
		principalRIDs: string[],
		newPermissions: string[],
		resourceRids: string[],
		presetsByType: PresetsByType[],
		availablePermissionsSetsByType: PermissionByType,
		userOrUserGroupRids?: string[],
		permissionExpiryInDays?: number | null
	) {
		const replacePostRequestPayload: ReplacePostRequest = {
			rids: resourceRids,
			principalRIDs,
			userOrUserGroupRids,
			permissions: newPermissions,
			availablePermissionSets: availablePermissionsSetsByType[resourceType],
			presets: getPreset(presetsByType!, resourceType),
			permissionExpiryInDays,
		};
		await PermissionsHttp.replace(replacePostRequestPayload);
	}

	return (
		<LinkBasedShareContext.Provider
			value={{
				setShareWithSpecificPeopleOpen,
				isShareWithPeopleModalOpen: shareWithSpecificPeopleOpen,
				parentContainerRid,
				linkRid,
				linkUrl,
				conditionalLinks,
				setConditionalLinks,
				isLinkBasedManageUserModalOpen,
				setIsLinkBasedManageUserModalOpen,
				createLink,
				expiryDate,
				setExpiryDate,
				latestVersionFlag,
				setLatestVersionFlag,
				waterMarkFlag,
				setWaterMarkFlag,
				notifyMeFlag,
				setNotifyMeFlag,
				createConditionalLink,
				createAndReplaceLink,
				createLinkButtonLoading,
				isViewConditionalOptionModalOpen,
				setIsViewConditionalOptionModalOpen,
				linkOptionsContentType,
				setLinkOptionsContentType,
				updateConditionalLinks,
				consumerPluginName,
				isManageUserTriggeredByExtensionSlot,
				accountLevelLinkSettings,
				configurationsByTypeForWatermarking,
				sharablePresetsByTypeForWatermarking,
				watermarkedResponse,
				isWatermarkingAvailable,
			}}
		>
			{children}
		</LinkBasedShareContext.Provider>
	);
};
